import React, { useEffect, useRef, useState } from "react";
import ModalShell from "./modals/ModalShell";
import ResendButton from "./modals/ResendButton";
import { askAgainText, normalizeResult } from "./waitText";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Simple message dialog: { title, body, primary?: {label, onClick}, secondary?: {label, onClick} }
export function NoticeDialog({ notice, onClose }) {
  if (!notice) return null;
  const run = (action) => () => {
    onClose();
    if (action && action.onClick) action.onClick();
  };
  return (
    <ModalShell
      title={notice.title}
      onClose={onClose}
      footer={
        <>
          {notice.secondary && (
            <button type="button" className="rg-modal-btn rg-modal-btn--ghost" onClick={run(notice.secondary)}>
              {notice.secondary.label}
            </button>
          )}
          <button type="button" data-autofocus className="rg-modal-btn rg-modal-btn--primary" onClick={run(notice.primary)}>
            {(notice.primary && notice.primary.label) || "OK"}
          </button>
        </>
      }
    >
      <p style={{ whiteSpace: "pre-line" }}>{notice.body}</p>
      {notice.resend ? <ResendButton onResend={notice.resend.onResend} seconds={notice.resend.seconds} /> : null}
    </ModalShell>
  );
}

// Forgot-password dialog. onSubmit(email) resolves "sent" or "rate_limited".
export function ForgotPasswordDialog({ open, initialEmail, onSubmit, onClose }) {
  const [email, setEmail] = useState("");
  const [stage, setStage] = useState("form"); // form | sent
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setEmail(initialEmail && initialEmail.includes("@") ? initialEmail : "");
      setStage("form");
      setError("");
      setBusy(false);
      setTimeout(() => inputRef.current && inputRef.current.focus(), 50);
    }
    // Only reset when the dialog opens, not while the user types in the login form behind it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!EMAIL_RE.test(value)) {
      setError("Enter the email you signed up with.");
      return;
    }
    setError("");
    setBusy(true);
    const result = normalizeResult(await onSubmit(value));
    setBusy(false);
    if (result.status === "rate_limited") {
      setError(
        result.retryAfter
          ? `You've asked for several reset emails. ${askAgainText(result.retryAfter)}`
          : "Too many requests. Please wait a few minutes and try again."
      );
      return;
    }
    setEmail(value);
    setStage("sent");
  };

  if (stage === "sent") {
    return (
      <ModalShell
        key="sent"
        title="Check your email"
        onClose={onClose}
        footer={
          <button type="button" data-autofocus className="rg-modal-btn rg-modal-btn--primary" onClick={onClose}>
            Done
          </button>
        }
      >
        <p>
          If an account exists for <strong>{email}</strong>, we've sent a link to reset your password. Check your inbox
          and spam folder.
        </p>
        <ResendButton onResend={() => onSubmit(email)} />
      </ModalShell>
    );
  }

  return (
    <ModalShell
      key="form"
      title="Reset your password"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="rg-modal-btn rg-modal-btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="fp-form" className="rg-modal-btn rg-modal-btn--primary" disabled={busy}>
            {busy ? "Sending..." : "Send reset link"}
          </button>
        </>
      }
    >
      <form id="fp-form" onSubmit={submit} noValidate>
        <p>Enter your email and we'll send you a link to choose a new password.</p>
        <label htmlFor="fp-email" className="rg-modal-label">Email</label>
        <input
          id="fp-email"
          ref={inputRef}
          data-autofocus
          className="rg-modal-field"
          type="email"
          autoComplete="email"
          placeholder="you@university.edu"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        {error && (
          <div className="rg-modal-error" role="alert">
            {error}
          </div>
        )}
      </form>
    </ModalShell>
  );
}
